import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import styled from 'styled-components/macro';
import Slider from 'react-slick';
import HtmlParser from 'html-react-parser';

import GlobalContext from '@explorer/modules/defines/contexts';

import Localization, { langsSelectList } from '@mirinae/shared/il8n';
import { pagePaths, cookieDomain, explorerPaths, authPaths } from '@mirinae/defines/paths';
import LangSelector from '@mirinae/react-gnb/elements/LangSelector';
import AnalyzerTopFixedBanner from '@mirinae/explorer/components/common/AnalyzerTopFixedBanner';
import TryFreeButton from '@mirinae/explorer/components/buttons/TryFreeButton';
import IconLogo from '@mirinae/react-gnb/assets/IconLogo';

import mobileImg from '@explorer/components/assets/landing/mobile-img.svg';
import devicesImg from '@explorer/components/assets/landing/devices.svg';
import rocketImg from '@explorer/components/assets/landing/rocket.svg';
import starImg from '@explorer/components/assets/landing/star-fill.svg';
import level0Img from '@explorer/components/assets/landing/level0.svg';
import level1Img from '@explorer/components/assets/landing/level1.svg';
import level2Img from '@explorer/components/assets/landing/level2.svg';
import level3Img from '@explorer/components/assets/landing/level3.svg';
import level4Img from '@explorer/components/assets/landing/level4.svg';
import level5Img from '@explorer/components/assets/landing/level5.svg';
import level6Img from '@explorer/components/assets/landing/level6.svg';
import starterImg from '@explorer/components/assets/landing/landing-starter.svg';
import quizImg from '@explorer/components/assets/landing/landing-quiz.svg';
import cultureImg from '@explorer/components/assets/landing/landing-culture.svg';
import animationImg from '@explorer/components/assets/landing/landing-animation.svg';
import availableChromeWebstoreImg from '@explorer/components/assets/landing/available-chrome-webstore.svg';
import idiomsProverbsAndSlangImg from '@explorer/components/assets/landing/idioms-proverbs-and-slang.svg';
import chromeExtensionImg from '@explorer/components/assets/landing/chrome-extension.png';
import IconSlideLeft from '@explorer/components/assets/landing/IconSlideLeft.png';
import IconSlideRight from '@explorer/components/assets/landing/IconSlideRight.png';
import IconSlideLeftDisabled from '@explorer/components/assets/landing/IconSlideLeftDisabled.png';
import IconSlideRightDisabled from '@explorer/components/assets/landing/IconSlideRightDisabled.png';
import linkRightImg from '@explorer/components/assets/landing/link-right.svg';

import searchImg from '@explorer/components/assets/landing/IconSearch.svg';
import libraryImg from '@explorer/components/assets/landing/IconLibrary.svg';
import parkOutlineImg from '@explorer/components/assets/landing/IconParkOutline.svg';
import unlock1Img from '@explorer/components/assets/landing/IconUnlocked1.svg';
import unlock2Img from '@explorer/components/assets/landing/IconUnlocked2.svg';
import parseTreeAnim from '@explorer/components/assets/landing/parse-tree-anim.svg';

import Reviews from '@mirinae/members/components/plan/2.0/Reviews';
import { authAPI } from '@mirinae/react-auth';
import GTM from '@mirinae/apis/GoogleAnalytics';
import { Button } from '@mirinae/react-gnb/gnbStyles';
import { palette } from '@mirinae/react-ui';

const LandingPageLayout = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: ${props => (props.showTopBanner ? 56 : 0)}px;
    width: 100%;
    @media only screen and (max-width: 430px) {
        padding: 56px 20px 0 20px;
    }
`;

const LogoAndLanguage = styled.div`
    position: fixed;
    max-width: 1200px;
    min-width: 280px;
    background: #fff;
    z-index: 8000;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 75px;
    width: 100%;
    @media only screen and (max-width: 430px) {
    }
`;

const Logo = styled(Link)`
`;

const Language = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 10px;
`;

const LangText = styled.div`
    color: var(--gray-gray-04, #9D9D9D);
    font-size: 14px;
    font-weight: 600;
    line-height: 26px; /* 185.714% */
    @media only screen and (max-width: 430px) {
        display: none;
    }
`;

const RightControls = styled.div`
    display: flex;
    flex-direction: row;
    z-index: 9000;
`;

const LoginButton = styled.button`
    line-height: 22px;
    color: ${palette.gray[1]};
    font-weight: 600;
    text-transform: capitalize;
    white-space: nowrap;
    margin: 0 5px 0 20px;
    ::before {
        content: '';
        padding-right: 20px;
        border-left: thin solid #DFDFDF;
    };
    :hover {
        color: ${palette.main.darken[1]};
    }
    @media only screen and (max-width: 430px) {
      margin: 0 2px 0 5px;
        ::before {
            padding-right: 10px;
        };
    }
`;
const SignupButton = styled.button`
    height: 38px;
    margin-left: 10px;
    padding: 0 20px;
    border-radius: 30px;  
    background-color: ${palette.main.main};
    color: ${palette.primary.white};
    font-size: 12px;
    font-weight: 700;
    text-transform: capitalize;
    white-space: nowrap;
    :hover {
        background-color: ${palette.main.darken[1]};
    }
`;


const ComprehensibleLearningRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 135px;
    margin-bottom: 75px;
    justify-content: space-between;
    @media only screen and (max-width: 430px) {
        width: 100%;
        flex-direction: column;
        margin-bottom: 35px;
    }
`;

const ComprehensibeLearning = styled.div`
    display: flex;
    flex-direction: column;
    width: 456px;
    justify-content: center;
    & span {
        color: var(--gray-gray-02, #626262);
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
        margin: 16px 0 30px 0;
    }
    @media only screen and (max-width: 430px) {
        width: 100%;
        & span {
            color: var(--gray-gray-02, #626262);
            font-size: 16px;
            font-weight: 400;
            line-height: 140%;
            margin: 16px 0 30px 0;
            text-align: center;
        }   
    }
`;

const Title = styled.div`
    color: var(--primary-black, #3C3C3B);
    font-size: 38px;
    font-weight: 700;
    line-height: 50px; /* 131.579% */
    @media only screen and (max-width: 430px) {
        color: var(--primary-black, #3C3C3B);
        font-size: 38px;
        text-align: center;
        font-family: Montserrat;
        font-style: normal;
        font-weight: 700;
    }
`;

const MobileImg = styled.div`
    display: flex;
    justify-content: center; 
    width: 424px;
    height: 473px;
    align-items: center; 
    img {
        width: auto;
        height: auto;
    }
    @media only screen and (max-width: 430px) {
        width: 100vw;
        margin-top: 50px;
        /* padding-top: 120px; */
        overflow: hidden;
        margin-left: -20px;
        height: 395px;
        img {
            transform: translate(0px, 50px);
        }
    }
`;

const GetStartedButton = styled.a`
    display: flex;
    width: 195px;
    height: 56px;
    padding: 10px 20px;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    background: var(--main-main, #87CBC8);
    color: #FFF;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px; /* 171.429% */
    &:hover {
        background: var(--main-main, #48A9A6);
    }
    @media only screen and (max-width: 430px) {
        width: 100%;
    }
`;

// const Stars = styled.div`
//     display: flex;
//     flex-direction: row;
// `;

const InformationRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 80px;
    justify-content: space-between;
    column-gap: 26px;
    p {
        text-align: center;
    }
    img {
        width: auto;
        height: auto;
    }
    @media only screen and (max-width: 430px) {
        width: 100%;
        align-items: center;
        flex-direction: column;
    }
`;

const AvailableDevicesRow = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center; 
    width: 190px;
    align-items: center; 
    img {
        margin-bottom: 8px;
    }
    @media only screen and (max-width: 430px) {
        height: 95px;
    }
`;

const GrammerPatterns = styled.div`
    display: flex;
    flex-direction: column;
    width: 256px;
    border-left: 1px dotted;
    border-right: 1px dotted;
    p.landing-title {
        font-size: 36px;
        font-weight: 500;
        margin-bottom: 8px;
    }
    @media only screen and (max-width: 430px) {
        border-left: 0;
        border-right: 0;
        width: 151px;
        height: 95px;
    }
`;

const StarsRow = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center; 
    width: 190px;
    img {
        margin-bottom: 8px;
    }
    @media only screen and (max-width: 430px) {
        order: 0;
        width: 140px;
        height: 95px;
    }
`;

const MobileRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center; 
    column-gap: 26px;
    @media only screen and (max-width: 430px) {
        order: 1;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center; 
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    p.landing-title {
        text-align: center;
        font-size: 28px;
        font-weight: 700;
        margin-bottom: 16px;
    }
    p.landing-subtext {
        color: var(--gray-gray-02, #626262);
        text-align: center;
        font-size: 18px;
        font-weight: 400;
        line-height: 150%; /* 27px */
        width: 670px;
        margin-bottom: 60px;
    }
    p.parsetree-subtext {
        margin-bottom: 0px;
    }
    @media only screen and (max-width: 430px) {
        width: 100%;
        p.landing-title {
            font-size: 24px;
        }
        p.landing-subtext {
            width: 100%;
        }
        img {
            width: 100%;
        }
    }
`;

const TwoColumn = styled.div`
    display: flex;
    flex-direction: column;
    width: 418px;
    & p {
        color: var(--gray-gray-02, #626262);
        font-size: 18px;
        font-weight: 400;
        line-height: 150%; /* 27px */    
    }
    @media only screen and (max-width: 430px) {
        width: 100%;
        text-align: center;
    }
`;

const TwoColumnRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 80px;
    column-gap: 60px;
    .chrome-extension {
        width: 541px;
        height: 301px;
    }
    @media only screen and (max-width: 430px) {
        width: 100%;
        flex-direction: column;
        row-gap: 60px;
        .chrome-extension {
            width: 100%;
            height: auto;
        }
    }
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 40px;
    align-items: center;
    justify-content: center;
`;

const ProIcon = styled.div`
    position: relative;
    top: -35px;
    img {
        width: auto;
        height: auto;
    }
`;

const ProColumn = styled.div`
    display: flex;
    flex-direction: column;
    width: 100vw;
    align-items: center;
    background-color: #F8F9FF;
    padding-bottom: 80px;
`;

const UnlimitedProRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 40px;
    justify-content: center;
    width: 700px;
    @media only screen and (max-width: 430px) {
        width: 100%;
    }
`;
const UnlimitedProColumn = styled.div`
    display: flex;
    flex-direction: column;
    width: 185px;
    height: 135px;
    justify-content: flex-start;
    align-items: center;
    & img {
        width: 60px;
        height: 60px;
    }
    & p {
        text-align: center;
    }
    @media only screen and (max-width: 430px) {
        width: 160px;
        height: 130px;
    }
`;

const LevelsRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    column-gap: 10px;
    flex-wrap: wrap;
    & img {
        width: 46px;
        height: 18px;
    }
`;

// const availableChromeWebStore = {
//     width: '541px',
//     height: '301px',
// };

const goChromeExtension = {
    width: '220px',
    height: '56px',
};

// const proColumnItemWidth = {
//     width: '185px',
//     textAlign: 'center',
// };

const ProTitle = styled.div`
    font-size: 32px;
    text-align: center;
    font-weight: 700;
    background: linear-gradient(90deg, #3499FF -70.26%, #3A3985 121.84%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
`;

const ProDescribe = styled.div`
    margin: 25px 0 40px 0;
    @media only screen and (max-width: 430px) {
        text-align: center;
    }
`;

const topBannerStyle = {
    position: 'fixed',
    width: '100%',
    top: 0,
    left: 0,
    right: 0,
};

const margin80px = {
    marginBottom: '80px',
};

const margin60px = {
    marginBottom: '60px',
};

const margin20px = {
    marginBottom: '20px',
}

const ReviewsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    margin-top: 60px;
    margin-bottom: 80px;
`;

const SliderWrapper = styled.div`
    .slick-slider {
        position: relative;
        width: 855px;
        height: 500px;

        .slick-track {
            display: flex;
            gap: 0 35px;
            padding-top: 20px;
        }
        .slick-list {
            overflow: hidden;
        }
        .slick-prev,
        .slick-next {
            position: absolute;
            z-index: 2;
            width: 81px;
            height: 81px;
            background-color: rgba(60, 60, 59, 0.6);
            :before {
                content: '';
            }
            :hover {
                // background-color: rgba(60, 60, 59, 0.8);
            }
            &.slick-disabled {
                cursor: default;
                // background-color: rgba(60, 60, 59, 0.1);
            }
        }
        .slick-prev {
            left: -81px;
            background: url(${IconSlideLeft}) no-repeat;
            &.slick-disabled {
                background: url(${IconSlideLeftDisabled}) no-repeat;
            }
        }
        .slick-next {
            right: -81px;
            background: url(${IconSlideRight}) no-repeat;
            &.slick-disabled {
                background: url(${IconSlideRightDisabled}) no-repeat;
            }
        }
    }
    @media only screen and (max-width: 430px) {
        .slick-slider {
            position: relative;
            width: 390px;
            height: 240px;
            .slick-track {
                display: flex;
                gap: 0 35px;
            }
            .slick-track img {
                width: 370px;
                height: 205px;
            }
            .slick-prev {
                left: -8px;
            }
            .slick-next {
                right: -8px;
            }
        }
    }
`;

const IdiomsProverbsTitle = styled.div`
    color: var(--primary-black, #3C3C3B);
    justify-content: flex-start;
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    margin-bottom: 20px;
`;

const LearnWhilePlayingTitle = styled.div`
    color: var(--primary-black, #3C3C3B);
    justify-content: flex-start;
    font-size: 28px;
    font-style: normal;
    font-weight: 700;
    margin-bottom: 20px;
`;

const SlideItem = styled.div`
    width: 820px;
    height: 450px;
    & img {
        width: 820px;
        height: 450px;
    }
`;

const ExplorerAllWords = styled.a`
    width: 195px;
    height: 56px;
    padding: 18px 20px;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    background: #F6F6F6;
    margin-top: 20px;
    margin-right: 10px;
    & img {
        margin-left: 10px;
        width: 4px;
        height: 8px;
    }
    @media only screen and (max-width: 430px) {
        width: 100%
    }
`;

const tryFreeButtonStyle = {
    width: '330px',
    height: '56px',
};

const LandingPageContainer = ({ }) => {
    const [globalData, setGlobalData] = useContext(GlobalContext);
    const [_, setCookie] = useCookies([]);
    const [selectedLang, setSelectedLang] = useState(navigator.language.replace(/-\w+.*/, '')); // default to the browser language for this user, stripping off any script subtag
    const [showTopBanner, setShowTopBanner] = useState(true);

    useEffect(() => {
        setCookie('landed', 'true', { path: '/', maxAge: 10 * 365 * 24 * 60 * 60, domain: cookieDomain });
    }, []);

    const onCloseClick = (e) => {
        setShowTopBanner(false);
    };

    const handleClickPrefs = (newLang) => {
        console.log(langsSelectList);
        const lang = newLang.language || 'en';
        const updatedPrefs = ({ ...globalData.prefs, language: lang });
        setGlobalData(prev => ({ ...prev, ...updatedPrefs }));
        setSelectedLang(lang);
        Localization.setLanguage(lang);
    };

    const settings = {
        dots: true,
        infinite: false,
        speed: 400,
        slidesToShow: 1,
        slidesToScroll: 1
    };

    return (
        <LandingPageLayout showTopBanner={showTopBanner}>
            { showTopBanner && <AnalyzerTopFixedBanner localization={Localization} style={topBannerStyle} onCloseClick={onCloseClick}/> }
            <LogoAndLanguage>
                <Logo reloadDocument to={pagePaths.home}><IconLogo /></Logo>
                <RightControls>
                    <Language>
                        <LangText>{HtmlParser(Localization['landing-site-language'])}:</LangText>
                        <LangSelector langsList={langsSelectList} selected={selectedLang} onClickPrefs={handleClickPrefs} />
                    </Language>
                    <LoginButton type="button" onClick={() => { window.location.href = `${authPaths.login}?redirect=${explorerPaths.home}` }}>{Localization['rp-login']}</LoginButton>
                    <SignupButton type="button" onClick={() => { window.location.href = `${authPaths.signup}?redirect=${explorerPaths.home}` }}>{Localization['auth-signup']}</SignupButton>
                </RightControls>
            </LogoAndLanguage>

            <ComprehensibleLearningRow>
                <ComprehensibeLearning>
                    <Title>{Localization['landing-comprehensible-language-learning']}</Title>
                    <span>{HtmlParser(Localization['landing-learning-a-language'])}</span>
                    <GetStartedButton href={`${authPaths.signup}?redirect=${explorerPaths.home}`}>{Localization['landing-get-started']}</GetStartedButton>
                </ComprehensibeLearning>
                <MobileImg><img src={mobileImg} alt="Mobile Phone"/></MobileImg>
            </ComprehensibleLearningRow>

            <InformationRow>
                <MobileRow>
                    <AvailableDevicesRow>
                        <img src={devicesImg} alt="" />
                        <p>{Localization['landing-available-on-any-device']}</p>
                    </AvailableDevicesRow>
                    <GrammerPatterns>
                        <p className="landing-title">2K+</p>
                        <p>{Localization['landing-korean-grammer-patterns']}</p>
                    </GrammerPatterns>
                </MobileRow>
                <StarsRow>
                    <img src={starImg} alt="Reviews" />
                    <p>{Localization['landing-reviews']}</p>
                    <p>{Localization['landing-reviews-2']}</p>
                </StarsRow>
            </InformationRow>

            <Column style={{ marginBottom: 35 }}>
                <p className="landing-title">{Localization['landing-decoding-korean-sentences']}</p>
                <p className="parsetree-subtext">{HtmlParser(Localization['landing-mirinae-explorer-uses'])}</p>
                <img src={parseTreeAnim} alt="Explorer Overview" />
            </Column>

            <Column style={margin80px}>
                <p className="landing-title">{Localization['landing-lessions-expanded']}</p>
                <p className="landing-subtext">{HtmlParser(Localization['landing-you-can-take-over'])}</p>
                <LevelsRow style={margin60px}>
                    <img src={level0Img} alt="Level 0" />
                    <img src={level1Img} alt="Level 1" />
                    <img src={level2Img} alt="Level 2" />
                    <img src={level3Img} alt="Level 3" />
                    <img src={level4Img} alt="Level 4" />
                    <img src={level5Img} alt="Level 5" />
                    <img src={level6Img} alt="Level 6" />
                </LevelsRow>
                <SliderWrapper>
                    <Slider {...settings}>
                        <SlideItem><img src={animationImg} alt="Animation" /></SlideItem>
                        <SlideItem><img src={starterImg} alt="Starter Animation" /></SlideItem>
                        <SlideItem><img src={cultureImg} alt="Culture Animation" /></SlideItem>
                        <SlideItem><img src={quizImg} alt="Quiz Animation" /></SlideItem>
                    </Slider>
                </SliderWrapper>
            </Column>

            <TwoColumnRow style={margin80px}>
                <img src={idiomsProverbsAndSlangImg} alt="Idioms, Proverbs, and Slang" />
                <TwoColumn>
                    <IdiomsProverbsTitle>{Localization['landing-idioms-proverbs-and-slang']}</IdiomsProverbsTitle>
                    <p>{HtmlParser(Localization['landing-you-can-easily-pick-up'])}</p>
                    <ExplorerAllWords href="/wikinae">{Localization['landing-explore-all-words']}<img src={linkRightImg} alt="Go to Wikinae" /></ExplorerAllWords>
                </TwoColumn>
            </TwoColumnRow>

            {/*<TwoColumnRow>*/}
            {/*    <TwoColumn>*/}
            {/*        <LearnWhilePlayingTitle>{Localization['landing-learn-while-playing']}</LearnWhilePlayingTitle>*/}
            {/*        <p style={margin20px}>{HtmlParser(Localization['landing-you-can-take-over-strong'])}</p>*/}
            {/*        <a href="https://chrome.google.com/webstore/detail/mirinae-for-chrome/nagkhplkbmnpanjfjpoepjmikmegcihp"><img src={availableChromeWebstoreImg} alt="Available Chrome WebStore" style={goChromeExtension} /></a>*/}
            {/*    </TwoColumn>*/}
            {/*    <img className="chrome-extension" src={chromeExtensionImg} alt="Go Chrome Extension" />*/}
            {/*</TwoColumnRow>*/}

            <ProColumn>
                <ProIcon><img src={rocketImg} alt="" /></ProIcon>
                <ProTitle>{Localization['landing-unlimited-korean-learning-with-pro']}</ProTitle>
                <ProDescribe>{Localization['landing-pro-provides-uninterrupted']}</ProDescribe>
                <UnlimitedProRow className="unlimited-pro">
                    <UnlimitedProColumn>
                        <img src={searchImg} alt="" />
                        <p>{Localization['landing-unrestricted-sentence-analyzer']}</p>
                    </UnlimitedProColumn>
                    <UnlimitedProColumn>
                        <img src={libraryImg} alt="" />
                        <p>{Localization['landing-full-access-to-pattern-library']}</p>
                    </UnlimitedProColumn>
                    <UnlimitedProColumn>
                        <img src={unlock1Img} alt="" />
                        <p>{Localization['landing-access-all-lessons']}</p>
                    </UnlimitedProColumn>
                    <UnlimitedProColumn>
                        <img src={parkOutlineImg} alt="" />
                        <p>{Localization['landing-ad-free']}</p>
                    </UnlimitedProColumn>
                    <UnlimitedProColumn>
                        <img src={searchImg} alt="" />
                        <p>{Localization['landing-unrestricted-sentence-analyzer']}</p>
                    </UnlimitedProColumn>
                    <UnlimitedProColumn>
                        <img src={unlock2Img} alt="" />
                        <p>{Localization['landing-enable-advanced-features']}</p>
                    </UnlimitedProColumn>
                </UnlimitedProRow>
                <TryFreeButton trialStatus="no-trial" style={tryFreeButtonStyle} />
            </ProColumn>

            <ReviewsWrapper>
                <Reviews />
            </ReviewsWrapper>
            
        </LandingPageLayout>
    );
};

export default LandingPageContainer;
